// The authenticated transport for the signed-in Human's `/session/*` endpoints,
// and this lib's own `sendRequest`. (Signing in, and what happens to a login
// with no account, are the identity server's — see identityServerClient.js.) It is a small, deliberate copy of the app SDK's transport
// rather than a shared import: a reusable identity client cannot depend up into
// a host's SDK, so it carries the ~40 lines it needs to talk to `/session/*`.
// The two wrappers stay consistent by staying small.
//
// `customHeaderFn` supplies the credential — it is called with the method and
// URL of each request, because a proof is made for that request and no other.
// `onUnauthorized` is the host's 401 handler (see unauthorizedHandler.js). Both
// are set once by `configure`.
//
// One request can be sent somewhere other than where it is addressed, and
// proved as a third URL again — see `sameOriginDevUrl` and
// `sameOriginDevProofUrl`. The address stays what the error and the log say.
import { sameOriginDevProofUrl, sameOriginDevUrl } from './identityServerClient.js';

function createIdentityClient() {
    let customHeaderFn = async function () { return {}; };
    let onUnauthorized = null;
    let baseUri;

    // The one seam every identity request passes through. GET carries no body
    // (some servers reject one). `statusCode` rides on the thrown error so the
    // session store can tell "signed out" (401) from "gone" (404) from an
    // outage. A 401 is surfaced to the host's handler, then rethrown, so a
    // broken handler cannot swallow it.
    async function sendRequest(url, method, body = {}) {
        const customHeaders = await customHeaderFn({ method, url: sameOriginDevProofUrl(url) });
        const response = await fetch(sameOriginDevUrl(url), {
            method,
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', ...customHeaders },
            redirect: 'follow',
            referrerPolicy: 'no-referrer',
            body: method === 'GET' ? undefined : JSON.stringify(body)
        });
        if (!response.ok) {
            const err = new Error(`Received response status code: ${response.status}`);
            err.statusCode = response.status;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.indexOf('application/json') !== -1) {
                err.body = await response.json();
            }
            if (response.status === 401 && typeof onUnauthorized === 'function') {
                try { onUnauthorized(err); } catch { /* swallow */ }
            }
            throw err;
        }
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');
        if (contentType && contentType.indexOf('application/json') !== -1) {
            return await response.json();
        }
        if (contentLength > 0) {
            return await response.text();
        }
        return undefined;
    }

    async function getMe() {
        return sendRequest(`${baseUri}/session/me`, 'GET');
    }

    async function updateMe(patch) {
        return sendRequest(`${baseUri}/session/me`, 'PATCH', patch);
    }

    async function getConnections() {
        return sendRequest(`${baseUri}/session/connections`, 'GET');
    }

    async function unlinkConnection(provider, subject) {
        const query = new URLSearchParams({ provider, subject });
        return sendRequest(`${baseUri}/session/connections?${query.toString()}`, 'DELETE');
    }

    async function closeAccount() {
        return sendRequest(`${baseUri}/session/close`, 'POST');
    }

    return {
        sendRequest,
        getMe,
        updateMe,
        getConnections,
        unlinkConnection,
        closeAccount,
        get baseUri() { return baseUri; },
        set baseUri(value) { baseUri = value; },
        set customHeaderFn(fn) { customHeaderFn = fn; },
        set onUnauthorized(fn) { onUnauthorized = fn; }
    };
}

export const identityClient = createIdentityClient();
export default identityClient;
